import * as React from "react";
import { Box, Stack } from "@mui/material";
import parse from "html-react-parser";
import dayjs from "dayjs";
import { ContentListItemType, ContentType } from "./ContentListItem.types";
import {
    AvatarContainer,
    AvatarStyled,
    ListItemAvatarContainer,
    ListItemButtonContainer,
    ListItemMainSection,
    ListItemTextHeadline,
    ListItemTextRelatedPost,
    ListItemTextSubline,
    TypographyHeaderSubTitleBox,
} from "./ContentListItem.styles";
import { highlightText } from "@infrastructure/Helpers/StringHelper";
import { prepareAvatarContent } from "../../../../../UserAvatar/UserAvatarFactory";
import { avatarFallbackColorSelector } from "@shared/Avatar/AvatarFallbackColorSelector";
import { CountsStack } from "../../../../../Post/SearchFallback/ContentListItem/ContentListItem.styles";
import { CommentSolidIcon } from "@icons/CommentSolidIcon";
import { GetReactionIcon } from "@shared/Components/Checktok/Reactions/ReactionsIconsFactory";
import { useMobile } from "@infrastructure/Hooks/useMobile";

dayjs.locale("de");

const aggregateReactions = (reactionMap: Record<string, number>) => {
    return Object.values(reactionMap).reduce(
        (accumulator, currentValue) => accumulator + currentValue,
        0,
    );
};

export function ContentListItem({
    content,
    onItemClick,
    stringsToHighlight,
}: ContentListItemType) {
    const { isMobile } = useMobile();
    const relativeDate = content.displayDate;

    const text = stringsToHighlight?.length
        ? highlightText(content.content, stringsToHighlight)
        : content.content;

    return (
        <ListItemButtonContainer
            onClick={() => onItemClick(content)}
            disableRipple={isMobile}
            showHover={!isMobile}
        >
            <ListItemMainSection>
                <ListItemAvatarContainer>
                    <AvatarContainer>
                        <AvatarStyled src={content.user.avatar ?? ""}>
                            {prepareAvatarContent(
                                avatarFallbackColorSelector(content.user.uuid),
                                content.user.gender,
                            )}
                        </AvatarStyled>
                    </AvatarContainer>
                </ListItemAvatarContainer>

                <Stack flexDirection="column" flex={1}>
                    <Stack flexDirection="row" justifyContent="space-between">
                        <ListItemTextHeadline>
                            {content.user.screenName ?? "[not set]"}
                        </ListItemTextHeadline>
                        <TypographyHeaderSubTitleBox>
                            {relativeDate}
                        </TypographyHeaderSubTitleBox>
                    </Stack>
                    {content.contentType === ContentType.COMMENT && (
                        <ListItemTextRelatedPost>
                            {content.parentContent}
                        </ListItemTextRelatedPost>
                    )}
                    <Stack flexDirection="row" alignItems="flex-start">
                        <Box flex={1}>
                            <ListItemTextSubline>
                                {text ? parse(text) : "[not set]"}
                            </ListItemTextSubline>
                        </Box>
                        {content.commentCount > 0 && (
                            <CountsStack>
                                <TypographyHeaderSubTitleBox>
                                    {content.commentCount}
                                </TypographyHeaderSubTitleBox>
                                <CommentSolidIcon
                                    width={"16px"}
                                    height={"16px"}
                                    color={
                                        "var(--community-color-light-steal-blue)"
                                    }
                                />
                            </CountsStack>
                        )}
                        {!content.commentCount && content.reactionCount && (
                            <CountsStack>
                                <TypographyHeaderSubTitleBox>
                                    {aggregateReactions(content.reactionCount)}
                                </TypographyHeaderSubTitleBox>
                                <Stack flexDirection="row">
                                    {Object.keys(content.reactionCount).map(
                                        (reactionName: string) => (
                                            <GetReactionIcon
                                                key={
                                                    String(content.id) +
                                                    reactionName
                                                }
                                                reactionName={reactionName}
                                                sx={{
                                                    height: "16px",
                                                    width: "16px",
                                                }}
                                            />
                                        ),
                                    )}
                                </Stack>
                            </CountsStack>
                        )}
                    </Stack>
                </Stack>
            </ListItemMainSection>
        </ListItemButtonContainer>
    );
}
